import React, { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { EDITOR_COLORS, editorText } from '../../../styles/editorTheme';

export interface OrderedOption {
  id: string;
  label: string;
}

interface OrderedDualListSelectorProps {
  options: OrderedOption[];
  selectedIds: string[];
  onChange: (nextSelectedIds: string[]) => void;
  availableTitle?: string;
  selectedTitle?: string;
}

const LISTBOX_VISIBLE_ROWS = 6;
const LISTBOX_ROW_HEIGHT = 20;
const LISTBOX_HEIGHT = LISTBOX_VISIBLE_ROWS * LISTBOX_ROW_HEIGHT;

const WEB_LISTBOX_STYLE = {
  width: '100%',
  border: `1px solid ${EDITOR_COLORS.controlBorder}`,
  borderRadius: '4px',
  backgroundColor: EDITOR_COLORS.background,
  color: EDITOR_COLORS.textPrimary,
  fontFamily: 'Arial',
  fontSize: '13px',
  lineHeight: '1.35',
  padding: '2px',
  boxSizing: 'border-box',
} as const;

const toUniqueOrdered = (ids: string[]): string[] => {
  const seen = new Set<string>();
  const ordered: string[] = [];
  ids.forEach((id) => {
    if (!seen.has(id)) {
      seen.add(id);
      ordered.push(id);
    }
  });
  return ordered;
};

const moveSelectedByOne = (
  ids: string[],
  selectedSet: Set<string>,
  direction: 'up' | 'down'
): string[] => {
  const reordered = [...ids];

  if (direction === 'up') {
    for (let i = 1; i < reordered.length; i += 1) {
      if (selectedSet.has(reordered[i]) && !selectedSet.has(reordered[i - 1])) {
        const temp = reordered[i - 1];
        reordered[i - 1] = reordered[i];
        reordered[i] = temp;
      }
    }
    return reordered;
  }

  for (let i = reordered.length - 2; i >= 0; i -= 1) {
    if (selectedSet.has(reordered[i]) && !selectedSet.has(reordered[i + 1])) {
      const temp = reordered[i + 1];
      reordered[i + 1] = reordered[i];
      reordered[i] = temp;
    }
  }
  return reordered;
};

export const OrderedDualListSelector: React.FC<OrderedDualListSelectorProps> = ({
  options,
  selectedIds,
  onChange,
  availableTitle = 'Custom Options',
  selectedTitle = 'Practice Options',
}) => {
  const isWeb = Platform.OS === 'web' || typeof document !== 'undefined';
  const [availableSelection, setAvailableSelection] = useState<string[]>([]);
  const [selectedSelection, setSelectedSelection] = useState<string[]>([]);

  const optionMap = useMemo(() => {
    const map = new Map<string, OrderedOption>();
    options.forEach((option) => {
      map.set(option.id, option);
    });
    return map;
  }, [options]);

  const selectedOrderedIds = useMemo(() => {
    return toUniqueOrdered(selectedIds.filter((id) => optionMap.has(id)));
  }, [optionMap, selectedIds]);

  const availableOptions = useMemo(() => {
    const selectedSet = new Set(selectedOrderedIds);
    return options.filter((option) => !selectedSet.has(option.id));
  }, [options, selectedOrderedIds]);

  useEffect(() => {
    const availableSet = new Set(availableOptions.map((option) => option.id));
    setAvailableSelection((current) => current.filter((id) => availableSet.has(id)));
  }, [availableOptions]);

  useEffect(() => {
    const selectedSet = new Set(selectedOrderedIds);
    setSelectedSelection((current) => current.filter((id) => selectedSet.has(id)));
  }, [selectedOrderedIds]);

  const onAdd = () => {
    if (availableSelection.length === 0) {
      return;
    }

    const availableSet = new Set(availableOptions.map((option) => option.id));
    const addIds = availableSelection.filter((id) => availableSet.has(id));
    if (addIds.length === 0) {
      return;
    }

    onChange(toUniqueOrdered([...selectedOrderedIds, ...addIds]));
    setAvailableSelection([]);
  };

  const onRemove = () => {
    if (selectedSelection.length === 0) {
      return;
    }

    const removeSet = new Set(selectedSelection);
    onChange(selectedOrderedIds.filter((id) => !removeSet.has(id)));
    setSelectedSelection([]);
  };

  const onMoveUp = () => {
    if (selectedSelection.length === 0) {
      return;
    }

    const selectedSet = new Set(selectedSelection);
    const firstSelectedIndex = selectedOrderedIds.findIndex((id) => selectedSet.has(id));
    if (firstSelectedIndex <= 0) {
      return;
    }

    onChange(moveSelectedByOne(selectedOrderedIds, selectedSet, 'up'));
  };

  const onMoveDown = () => {
    if (selectedSelection.length === 0) {
      return;
    }

    const selectedSet = new Set(selectedSelection);
    let lastSelectedIndex = -1;
    for (let i = selectedOrderedIds.length - 1; i >= 0; i -= 1) {
      if (selectedSet.has(selectedOrderedIds[i])) {
        lastSelectedIndex = i;
        break;
      }
    }

    if (lastSelectedIndex < 0 || lastSelectedIndex >= selectedOrderedIds.length - 1) {
      return;
    }

    onChange(moveSelectedByOne(selectedOrderedIds, selectedSet, 'down'));
  };

  const selectedSet = new Set(selectedSelection);
  const selectedIndexes = selectedOrderedIds
    .map((id, index) => (selectedSet.has(id) ? index : -1))
    .filter((index) => index >= 0);
  const minSelectedIndex = selectedIndexes.length > 0 ? Math.min(...selectedIndexes) : -1;
  const maxSelectedIndex = selectedIndexes.length > 0 ? Math.max(...selectedIndexes) : -1;
  const isMoveUpDisabled = selectedIndexes.length === 0 || minSelectedIndex <= 0;
  const isMoveDownDisabled = selectedIndexes.length === 0 || maxSelectedIndex >= selectedOrderedIds.length - 1;

  const renderListBox = (
    listOptions: OrderedOption[],
    selectedValues: string[],
    onSelectionChange: (ids: string[]) => void
  ) => {
    if (isWeb) {
      return React.createElement(
        'select' as any,
        {
          multiple: true,
          size: LISTBOX_VISIBLE_ROWS,
          'data-listbox': 'html-web',
          value: selectedValues,
          onChange: (event: any) => {
            const values = Array.from(event.target.selectedOptions || []).map((option: any) => String(option.value));
            onSelectionChange(values);
          },
          style: WEB_LISTBOX_STYLE as any,
        },
        listOptions.map((option) => React.createElement('option' as any, { key: option.id, value: option.id }, option.label))
      );
    }

    const selectedSet = new Set(selectedValues);

    return (
      <View style={styles.nativeListbox}>
        {listOptions.map((option) => {
          const isSelected = selectedSet.has(option.id);
          return (
            <Pressable
              key={option.id}
              style={[styles.nativeListItem, isSelected && styles.nativeListItemSelected]}
              onPress={() => {
                if (isSelected) {
                  onSelectionChange(selectedValues.filter((id) => id !== option.id));
                } else {
                  onSelectionChange([...selectedValues, option.id]);
                }
              }}
            >
              <Text style={styles.nativeListItemText}>{option.label}</Text>
            </Pressable>
          );
        })}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.listPanel}>
        {availableTitle ? <Text style={styles.panelTitle}>{availableTitle}</Text> : null}
        {renderListBox(availableOptions, availableSelection, setAvailableSelection)}
      </View>

      <View style={styles.transferButtonsCol}>
        <Pressable style={[styles.actionBtn, availableSelection.length === 0 && styles.actionBtnDisabled]} onPress={onAdd}>
          <Text style={styles.actionBtnText}>Add {'>>'}</Text>
        </Pressable>
        <Pressable style={[styles.actionBtn, selectedSelection.length === 0 && styles.actionBtnDisabled]} onPress={onRemove}>
          <Text style={styles.actionBtnText}>{'<<'} Remove</Text>
        </Pressable>
      </View>

      <View style={styles.listPanel}>
        {selectedTitle ? <Text style={styles.panelTitle}>{selectedTitle}</Text> : null}
        {renderListBox(
          selectedOrderedIds.map((id) => optionMap.get(id)).filter((option): option is OrderedOption => !!option),
          selectedSelection,
          setSelectedSelection
        )}
      </View>

      <View style={styles.orderButtonsCol}>
        <Pressable style={[styles.actionBtn, isMoveUpDisabled && styles.actionBtnDisabled]} onPress={onMoveUp}>
          <Text style={styles.actionBtnText}>Move Up</Text>
        </Pressable>
        <Pressable style={[styles.actionBtn, isMoveDownDisabled && styles.actionBtnDisabled]} onPress={onMoveDown}>
          <Text style={styles.actionBtnText}>Move Down</Text>
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 10,
  },
  listPanel: {
    flex: 1,
  },
  panelTitle: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    marginBottom: 4,
  },
  nativeListbox: {
    height: LISTBOX_HEIGHT,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 4,
    backgroundColor: EDITOR_COLORS.background,
    paddingVertical: 0,
  },
  nativeListItem: {
    minHeight: LISTBOX_ROW_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 8,
    paddingVertical: 1,
  },
  nativeListItemSelected: {
    backgroundColor: '#eff6ff',
  },
  nativeListItemText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  transferButtonsCol: {
    width: 86,
    justifyContent: 'center',
    gap: 8,
  },
  orderButtonsCol: {
    width: 96,
    justifyContent: 'center',
    gap: 8,
  },
  actionBtn: {
    height: 28,
    minWidth: 84,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  actionBtnDisabled: {
    opacity: 0.5,
  },
  actionBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    textAlign: 'center',
  },
});
